import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cpu, Save } from "lucide-react";
import { toast } from "sonner";

export const SettingsModal = ({ open, onClose, settings, onSave }) => {
  const [engineDepth, setEngineDepth] = useState("18");
  const [stockfishPath, setStockfishPath] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setEngineDepth(String(settings.engine_depth || 18));
      setStockfishPath(settings.stockfish_path || "");
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        preferred_provider: "engine",
        engine_depth: Number(engineDepth) || 18,
        stockfish_path: stockfishPath.trim() || null,
      });
      toast.success("Engine settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md" data-testid="settings-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white">Engine Settings</DialogTitle>
          <DialogDescription className="text-white/50">
            The assistant is now engine-backed. It refuses to invent moves without Stockfish.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm text-emerald-200">
            <div className="flex items-center gap-2 font-medium">
              <Cpu size={16} />
              Engine-first mode
            </div>
            <p className="mt-2 text-emerald-100/80">
              Move choice comes from Stockfish. The UI only presents engine-backed analysis and principal variations.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Engine Depth</Label>
            <Input
              type="number"
              min="8"
              max="24"
              value={engineDepth}
              onChange={(e) => setEngineDepth(e.target.value)}
              className="bg-transparent border-white/20 text-white"
              data-testid="engine-depth-input"
            />
            <p className="text-xs text-white/30">
              Higher depth is stronger but slower. `18` is the default for live play.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Stockfish Path</Label>
            <Input
              value={stockfishPath}
              onChange={(e) => setStockfishPath(e.target.value)}
              placeholder="Optional override, e.g. /usr/games/stockfish"
              className="bg-transparent border-white/20 text-white font-mono text-sm"
              data-testid="stockfish-path-input"
            />
            <p className="text-xs text-white/30">
              Leave blank to auto-detect the system Stockfish binary.
            </p>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] btn-gold-glow"
            data-testid="save-settings-btn"
          >
            <Save size={14} className="mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
